<template>
    <div
        class="shrink-0 relative z-[1001] lg:!z-[1000] bg-primary-blue-hover"
        :class="[isHeaderIniting && 'header-initing']"
    >
        <PtAIHistoryNav
            v-model="opened"
            :menus="realNavMenuList"
            :historyMenu="navHistory"
            :messages="historyPreTitleMessages"
            responsive
            @clickHistory="viewResult"
            @removeHistory="onRemoveHistory"
        >
            <!-- pc端logo -->
            <template #productLogo="{ opened }">
                <div v-if="isHeaderIniting" class="common-nav-wide-logo w-full py-5 relative min-h-[72px] flex items-center justify-center">
                    <div class="bg-transparent w-[196px] h-[32px]"></div>
                </div>
                <a
                    v-else-if="headerService.whiteLabel && headerService.customLogoVisible"
                    class="common-nav-wide-logo w-full py-5 relative custom-logo min-h-[72px]"
                    :href="headerService.logoHref"
                >
                    <span class="flex justify-center">
                        <img
                            v-if="opened"
                            :src="headerService.customLogoUrl"
                            class="custom-logo-img"
                        />
                        <img
                            v-else
                            :src="headerService.customLogoUrl"
                            class="w-6 h-6 object-contain"
                        />
                    </span>
                </a>
                <a
                    v-else
                    href="/home"
                    class="common-nav-wide-logo w-full py-5 relative"
                >
                    <span class="flex" :class="{'justify-center': !opened, 'justify-start': opened}">
                        <PtIcon v-if="opened" :icon="Logo" class="!w-[100px] !h-[30px]"/>
                        <PtIcon v-else :icon="MiniLogo" class="!w-6 !h-6"/>
                    </span>
                </a>
            </template>

            <!-- 移动端logo -->
            <template #logo>
                <a
                    v-if="headerService.whiteLabel && headerService.customLogoVisible"
                    :href="headerService.logoHref"
                >
                    <img
                        :src="headerService.customLogoUrl"
                        alt="custom-logo"
                        class="custom-logo-img mobile"
                    >
                </a>
                <a v-else href="/home">
                    <PtIcon :icon="Logo" class="!w-[100px] !h-[30px]"/>
                </a>
            </template>
            <template #avatar="{ opened }">
                <div class="eureka-nav-bar-avatar mt-auto sticky bottom-0 bg-primary-blue-hover px-3 cursor-default mt-auto border-solid border-white/20 flex flex-row items-center gap-4 lg:border-t lg:flex-col lg:py-4">
                    <Incognito v-if="incognitoVisible" class="w-full" :opened="opened" :menu="incognitoItem" @handleClick="removeAllHistory"/>
                    <EditionTag v-if="isLogin && isFreemium" :open="opened" />

                    <PtNavUserAvatar :open="opened" :service="headerService" @clickLogin="handleLogin">
                        <template #userPackageType>
                            <UserPackageType />
                        </template>
                    </PtNavUserAvatar>
                </div>
            </template>
        </PtAIHistoryNav>

        <MobileTag v-if="isLogin && isCompany" />

        <!-- Search Task Dialog -->
        <SearchTaskDialog
            ref="searchTaskDialogRef"
            :tasks="sessionsTaskData"
            @task-click="handleSearchTaskClick"
            @new-task="handleNewSearchTask"
            @search="handleSearchTask"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { PtAIHistoryNav, PtNavUserAvatar, PtIcon } from 'patsnap-biz';
import { type HeaderService, config, Site, SLD } from '@patsnap-utils/biz';
import Incognito from './components/Incognito.vue';
import SearchTaskDialog from './components/SearchTaskDialog.vue';
import { cn, tw, en, jp } from './infrastructure/locales';
import { INCOGNITO_MENU_ITEM } from './constants';
import { useNavBarState } from './composables/use-navbar-store';
import type { HistoryRowData, NAV_MENU_TYPE } from './types';
import { useNavBar } from './composables/use-nav-bar';
import { get } from 'lodash-es';
import { usePlg } from '@eureka/shared/plg-plan/src/composables/use-plg';
import { useRoute, useRouter } from 'vue-router';

import '@patsnap-ui/icon/assets/solid/CloseMedium.svg'
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import '@patsnap-ui/icon/assets/solid/AddLarge.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/solid/NavWorkspace.svg'
import '@patsnap-ui/icon/assets/solid/ChartTopipc.svg'
import '@patsnap-ui/icon/assets/solid/ChartRing.svg'
import '@patsnap-ui/icon/assets/solid/FolderMany.svg';
import '@patsnap-ui/icon/assets/solid/InsightsTechnology.svg'
import Logo from './assets/logo.svg';
import MiniLogo from './assets/mini-logo.svg';

// 🚀 性能优化：异步加载非关键组件
const EditionTag = defineAsyncComponent(() => import('@eureka/shared/edition-upgrade/src/components/edition/EditionTag.vue'));
const MobileTag = defineAsyncComponent(() => import('@eureka/shared/edition-upgrade/src/components/edition-tag/MobileTag.vue'));
const UserPackageType = defineAsyncComponent(() => import('@eureka/shared/header-menu-3/src/UserPackageType.vue'));

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
});

const props = defineProps<{
    headerService: HeaderService,
    login?: () => void,
    navModuleType: NAV_MENU_TYPE
}>();

const route = useRoute();
const router = useRouter();

const { opened, getSessionsTaskData, sessionsTaskData } = useNavBarState();

const searchTaskDialogRef = ref<InstanceType<typeof SearchTaskDialog> | null>(null);

const { historyPreTitleMessages, viewResult, onRemoveHistory, removeAllHistory, navMenuList, navHistory } = useNavBar(props as any, searchTaskDialogRef);

const { isCompany, isFreemium } = usePlg();

const isLogin = computed(() => {
    return props.headerService.userCenterService.logged;
});

const realNavMenuList = computed(() => {
    return navMenuList.value
})

props.headerService.userCenterService.beforeLogoutHandlers.add(function() {
    const service = props.headerService.userCenterService;
    service.logged = false;
    service.setUserInfo();
    localStorage.removeItem('patsnap-authorizations');
    const { href } = new Site({ sld: SLD.PASSPORT, pathname: 'logout' });
    const guestPage = `${location.origin}/home`;
    location.href = `${href}?from=${config.clientName}&redirect_uri=${guestPage}`;
    throw new Error('Logout in NavBar');
})

const isHeaderIniting = ref(!!props.headerService.deferred.promise);

props.headerService.deferred.promise.then(() => {
    isHeaderIniting.value = false;
});

const incognitoItem = computed(() => {
    return {
        ...INCOGNITO_MENU_ITEM,
        title: t(`NavBar.${INCOGNITO_MENU_ITEM.type}`),
        tip: t(`NavBar.${INCOGNITO_MENU_ITEM.type}_TIP`),
    };
});

const incognitoVisible = computed(() => {
    return get(props.headerService.account, '_info.incognito_flag');
});

function handleLogin() {
    if (props.login) {
        props.login();
        return
    }
    import('@eureka/shared/login-sdk').then(({ loginSDK }) => {
        loginSDK.open().then(() => {
            window.location.reload()
        });
    });
}

function handleSearchTaskClick(task: HistoryRowData) {
    window.location.href = `/ai-search/${task.id}?query=${task.message}`;
}

function handleSearchTask(query: string) {
    getSessionsTaskData(query);
}

function handleNewSearchTask() {
    if (route.name === 'rd-home') {
        router.push({ name: 'rd-home' });
    } else {
        window.location.href = '/rd-home';
    }
}

</script>

<style lang="scss">
.common-nav-wide-logo {
    // &::after {
    //     content: '';
    //     @apply absolute left-3 right-3 bottom-0 h-px bg-white/20;
    // }

    &.custom-logo {
        .custom-logo-img {
            max-height: 48px;
            max-width: 100%;
            height: auto;
            width: auto;
            @apply transition-all duration-200;

            &.mobile {
                max-height: 24px !important;
                width: auto;
                height: 24px !important;
                object-fit: contain;
                @apply transition-all duration-200;
            }
        }
    }
}
.nav-history-sub-item {
    &.active-menu-item, &.tippy-content-active {
        span:last-child {
           display: none !important;
        }
    }
}

// 添加移动端特定样式
.patsnap-biz-mobile-nav-bar {
    .custom-logo-img.mobile {
        max-height: 24px !important;
        height: 24px !important;
        width: auto;
        object-fit: contain;
    }
}

.header-initing {
    .patsnap-biz-nav-header, .eureka-nav-bar-avatar {
        display: none;
    }
}
</style>
